import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { type DiscrepancyEventsQuery } from "generated-graphql/graphql";
import { prettyPrintDateTimeMed } from "util/dates";

import { DetailBlockLabel } from "./discrepancyDetailBlocks";
import { describeDiscrepancyEvent } from "./discrepancyEventDisplay";

export type DiscrepancyEventNode =
  DiscrepancyEventsQuery["manifestDiscrepancies"][number]["events"][number];

function EventHistoryLine({
  event,
  previous,
  field,
}: {
  event: DiscrepancyEventNode;
  previous?: DiscrepancyEventNode;
  field: string;
}) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {/* The app's formatter, so an event log reads like every other timestamp
            in oak — and to the minute: seconds are noise on a review log. */}
        {prettyPrintDateTimeMed(event.createdAt)} —{" "}
        {describeDiscrepancyEvent(event, field, previous)}
      </Typography>
      {event.resolutionNote && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 2 }}>
          Note: {event.resolutionNote}
        </Typography>
      )}
    </Box>
  );
}

/**
 * One discrepancy's event history, shared by both row expanders so the two
 * cannot drift on the loading skeleton or the error copy.
 *
 * The events arrive as a prop rather than from a query of this component's
 * own. The row expander shell fetches them, because the same history decides
 * whether "Undo resolution" is offered, and the buttons render above this
 * block: a second query for the same rows would let the two disagree.
 */
export function DiscrepancyEventHistory({
  events,
  field,
  error,
}: {
  /** Oldest first. `undefined` while the query has produced nothing yet. */
  events: readonly DiscrepancyEventNode[] | undefined;
  /** The discrepancy's field, for formatting a typed value. */
  field: string;
  error?: boolean;
}) {
  if (error) {
    return (
      <Typography variant="body2" color="text.secondary">
        Event history could not be loaded.
      </Typography>
    );
  }
  if (!events) return <Skeleton variant="text" width={240} />;
  if (events.length === 0) return null;

  return (
    <Box>
      {/* Labelled, because unlabelled timestamps sitting under the action row
          read as a caption on the buttons rather than a log of their own. */}
      <DetailBlockLabel>History</DetailBlockLabel>
      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
        {events.map((event, i) => (
          <EventHistoryLine key={event.id} event={event} previous={events[i - 1]} field={field} />
        ))}
      </Stack>
    </Box>
  );
}
